// frontend/components/writerCoachPanel/WriterCoachPanel.jsx
import React, { useState } from "react";
import { useContext } from "react";
import { Context } from "../../context/Context";
import "./writerCoachPanel.css";

export default function WriterCoachWidget({
  getArticleText,
  title,
  categories,
  keyphrase,
  stageKey,
}) {
  const [loading, setLoading] = useState(false);
  const [strengths, setStrengths] = useState([]);
  const [focusAreas, setFocusAreas] = useState([]);
  const [issues, setIssues] = useState([]);
  const [error, setError] = useState(null);
  const [lastAnalyzedAt, setLastAnalyzedAt] = useState(null);

  const { user: currentUser } = useContext(Context);

  const coachHeading =
    currentUser?.role === 2
      ? "Writing Coach (Beta)"
      : currentUser?.role === 3
      ? "Editing Coach (Beta)"
      : currentUser?.role >= 4
      ? "Editorial Coach (Beta)"
      : "Coach (Beta)";

  const capitalize = (str) =>
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

  const handleAnalyze = async () => {
    if (!getArticleText) return;
    const articleText = getArticleText() || "";

    if (!articleText.trim()) {
      setError("There’s no text to analyze yet.");
      return;
    }

    const apiBase = (process.env.REACT_APP_BACKEND_API || "").trim();

    if (!apiBase) {
      setError("Coach is misconfigured (missing REACT_APP_BACKEND_API).");
      return;
    }

    setLoading(true);
    setError(null);

    const url = `${apiBase.replace(/\/$/, "")}/ai/coach-summary`; // avoid double/missing slashes

    // Optional: timeout so "hung" requests become diagnosable
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          articleText,
          title,
          categories,
          keyphrase,
          feedbackFor:
            currentUser?.role === 2
              ? "WRITER"
              : currentUser?.role === 3
              ? "EDITOR"
              : currentUser?.role >= 4
              ? "LEAD_EDITOR"
              : "WRITER",
          stageKey,
        }),
        signal: controller.signal,
      });

     // Try to read a JSON error body if present (many APIs return { message, code, ... })
      const contentType = res.headers.get("content-type") || "";
      const isJson = contentType.includes("application/json");

      const payload = isJson ? await res.json().catch(() => null) : await res.text().catch(() => "");

      if (!res.ok) {
        const debugId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

        // Prefer backend-provided message if available, otherwise map by status
        const backendMsg =
          (payload && typeof payload === "object" && (payload.message || payload.error)) || "";

        const statusHint =
          res.status === 400 ? "Invalid request." :
          res.status === 401 ? "You may be signed out." :
          res.status === 403 ? "You don’t have permission." :
          res.status === 413 ? "Draft is too large." :
          res.status === 429 ? "Too many requests right now." :
          res.status >= 500 ? "Server error." :
          "Request failed.";

        // User-facing error (actionable, but not a wall of tech)
        setError(
          `${statusHint} ${backendMsg ? backendMsg : "Please try again."} (Ref: ${debugId})`
        );

        // Developer-facing log (the real gold)
        console.error("WriterCoachWidget request failed", {
          debugId,
          url,
          status: res.status,
          statusText: res.statusText,
          payload,
        });

        return;
      }

      // Guard: success responses must be JSON objects
      if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
        const debugId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

        setError(`Unexpected response format from coach service. (Ref: ${debugId})`);

        console.error("WriterCoachWidget unexpected success payload", {
          debugId,
          url,
          status: res.status,
          statusText: res.statusText,
          contentType,
          payload,
        });

        return;
      }

      // Success: payload is JSON object
      const data = payload; // already parsed
      setStrengths(data?.strengths || []);
      setFocusAreas(data?.focusAreas || []);
      setIssues(data?.issues || []);
      setLastAnalyzedAt(new Date());
    } catch (err) {
      const debugId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

      let message = "Couldn’t reach the coach service.";
      const msg = String(err?.message || "");
      if (err?.name === "AbortError") {
        message = "Coach request timed out (30s).";
      } else if (msg.includes("Failed to fetch") || msg.includes("NetworkError")) {
        message = "Network or CORS error while contacting the coach service.";
      }

      setError(`${message} (Ref: ${debugId})`);

      console.error("WriterCoachWidget error", {
        debugId,
        url,
        errName: err?.name,
        errMessage: err?.message,
        errStack: err?.stack,
      });
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  return (
    <div className="writer-coach-widget">
      <div className="writer-coach-header">
        <div>
          <h3>{coachHeading}</h3>
          <p className="writer-coach-subtitle">
            AI-powered, high-level feedback. No rewriting.
          </p>
        </div>
        <button
          onClick={handleAnalyze}
          disabled={loading}
          className="writer-coach-button"
        >
          {loading ? "Analyzing…" : "Analyze Draft"}
        </button>
      </div>

      {lastAnalyzedAt && (
        <div className="writer-coach-meta">
          <span className="writer-coach-meta">
            Last analyzed: {lastAnalyzedAt.toLocaleTimeString()}
          </span>
        </div>
      )}

      <div className="writer-coach-meta-row">
        {stageKey && (
          <span className="writer-coach-chip">
            Stage: {stageKey
              .replace(/_/g, " ")
              .split(" ")
              .map(capitalize)
              .join(" ")}
          </span>
        )}
        <span className="writer-coach-chip role-chip">
          Role: {currentUser?.role === 2
            ? "Writer"
            : currentUser?.role === 3
            ? "Editor"
            : "Lead Editor"}
        </span>
        {Array.isArray(categories) &&
          categories.slice(0, 2).map((cat, idx) => (
            <span key={idx} className="writer-coach-chip category-chip">
              {typeof cat === "string"
                ? cat
                : cat?.label || cat?.value || "Unlabeled"}
            </span>
          ))}
        {Array.isArray(categories) && categories.length > 2 && (
          <span className="writer-coach-chip category-chip">
            +{categories.length - 2}
          </span>
        )}
      </div>

      {error && <div className="writer-coach-error">{error}</div>}

      <div className="writer-coach-sections">
        <div className="writer-coach-section">
          <h4>Strengths</h4>
          {strengths.length === 0 && !loading && (
            <p className="writer-coach-empty">No feedback yet.</p>
          )}
          <ul className="writer-coach-list">
            {strengths.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </div>

        <div className="writer-coach-section">
          <h4>Focus Areas</h4>
          {focusAreas.length === 0 && !loading && (
            <p className="writer-coach-empty">No focus areas yet.</p>
          )}
          <ul className="writer-coach-list">
            {focusAreas.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
        </div>
      </div>

      <div className="writer-coach-section">
        <h4>Potential Areas of Improvement</h4>
        {issues.length === 0 && !loading && (
          <p className="writer-coach-empty">No issues flagged yet.</p>
        )}
        <ul className="writer-coach-issues">
          {issues.map((issue, i) => (
            <li key={i} className={`wc-issue wc-issue-${issue.type}`}>
              <div className="wc-issue-header">
                <span className="wc-issue-tag">
                  Type: {issue.type.replace("_", " ")}
                </span>
              </div>
              <div className="wc-issue-message">{issue.message}</div>
              <div className="wc-issue-prompt">
                <span className="wc-issue-prompt-label">Question</span>
                <span>{issue.coachPrompt}</span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
